import { Download, Smartphone, MonitorPlay, ShieldCheck, ArrowLeft, Tv } from "lucide-react";
import { Link } from "react-router-dom";

// Direct CDN link for the signed Android APK (Fire Stick / Fire TV / Android TV / phone).
const APK_URL =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_creative-core-4/artifacts/eae51e45143f3cbe_creative%20core%20v6.apk";

// Tiny inline QR generator (uses Google Charts — no external lib needed).
const qrSrc = (url) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=8&data=${encodeURIComponent(url)}`;

export default function DownloadAppPage() {
  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900" data-testid="download-page">
      <div className="max-w-5xl mx-auto px-6 py-10 lg:py-16">
        {/* Top nav */}
        <div className="flex items-center justify-between mb-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors"
            data-testid="download-back-link"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.25} />
            Volver al inicio
          </Link>
          <div className="font-heading text-lg tracking-tight">CreatiCore</div>
        </div>

        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12 items-start">
          {/* Left — Hero + CTA */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 text-xs uppercase tracking-[0.2em] text-slate-500 mb-6">
              <Tv className="h-3.5 w-3.5" strokeWidth={1.5} />
              Fire TV · Android TV · Android
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Descarga la app
              <br />
              <span className="text-slate-500">para tus pantallas</span>
            </h1>
            <p className="mt-5 text-base text-slate-600 max-w-md">
              Instálala una vez en tu Fire Stick o Android TV. Después solo inicia
              sesión con el usuario de la TV — la app se queda corriendo sola y
              reproduce tu contenido sin tocar el control.
            </p>

            <a
              href={APK_URL}
              download="CreatiCoreV6.apk"
              className="mt-9 inline-flex items-center gap-3 bg-slate-900 text-white px-6 py-3.5 rounded-xl text-base font-semibold shadow-sm hover:opacity-95 transition-opacity"
              data-testid="download-apk-button"
            >
              <Download className="h-5 w-5" strokeWidth={1.75} />
              Descargar CreatiCore V6 .apk
            </a>

            <div className="mt-4 text-xs text-slate-500">
              Versión V6 · Android · Listo para Fire Stick y Android TV
            </div>

            {/* Quick install steps */}
            <div className="mt-10 grid sm:grid-cols-3 gap-4">
              <Step
                n="1"
                icon={<ShieldCheck className="h-5 w-5" strokeWidth={1.5} />}
                title="Permitir orígenes desconocidos"
                body="Settings → My Fire TV → Developer Options → Apps from Unknown Sources: ON"
              />
              <Step
                n="2"
                icon={<Smartphone className="h-5 w-5" strokeWidth={1.5} />}
                title="Descargar el APK"
                body="Usa el navegador del Fire Stick o instala 'Downloader' y abre creativecorenj.com/download"
              />
              <Step
                n="3"
                icon={<MonitorPlay className="h-5 w-5" strokeWidth={1.5} />}
                title="Abrir e iniciar sesión"
                body="Ingresa el usuario y contraseña de la TV — empieza a reproducir en automático."
              />
            </div>
          </div>

          {/* Right — QR card */}
          <div className="lg:sticky lg:top-10">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">
                Escanea con tu celular
              </div>
              <div className="aspect-square bg-slate-50 rounded-xl overflow-hidden flex items-center justify-center">
                <img
                  src={qrSrc(APK_URL)}
                  alt="QR para descargar la APK"
                  className="w-full h-full object-contain"
                  data-testid="download-qr"
                />
              </div>
              <p className="mt-4 text-xs text-slate-500 leading-relaxed">
                Apunta la cámara del Fire Stick (o de tu teléfono) y abre el
                enlace. El APK se descarga directo desde el CDN.
              </p>
            </div>

            <div className="mt-4 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900">
              <strong className="font-semibold">Tip Fire Stick:</strong> instala
              la app gratuita <em>"Downloader"</em> desde Amazon Appstore. Abre
              Downloader y escribe: <code className="font-mono bg-amber-100 px-1.5 py-0.5 rounded">creativecorenj.com/download</code>
            </div>
          </div>
        </div>

        <footer className="mt-16 pt-8 border-t border-slate-200 text-xs text-slate-500 flex flex-wrap items-center justify-between gap-3">
          <span>© CreatiCore 2026 · Built for hospitality</span>
          <div className="flex gap-4">
            <Link to="/login" className="hover:text-slate-900">Admin login</Link>
            <Link to="/tv-login" className="hover:text-slate-900">TV login</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}

function Step({ n, icon, title, body }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 text-slate-900">
        <span className="text-xs font-mono text-slate-400">0{n}</span>
        {icon}
      </div>
      <div className="mt-2 font-semibold text-sm">{title}</div>
      <div className="mt-1 text-xs text-slate-500 leading-relaxed">{body}</div>
    </div>
  );
}
